import React from 'react';
import { Clock, Search } from 'lucide-react';
import { formatDistanceInTz } from '../../utils/dateFormatters';
import type { PendingTaskItem } from '../../hooks/useTasksQueue';

interface LeadGenNextActionsPanelProps {
  pendingTasks: PendingTaskItem[];
  hasAnyTasks: boolean;
}

const LeadGenNextActionsPanel: React.FC<LeadGenNextActionsPanelProps> = ({
  pendingTasks,
  hasAnyTasks,
}) => {
  const displayActions = pendingTasks.slice(0, 6);

  if (!hasAnyTasks && displayActions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-[#05E0D1]/5 rounded-xl border-2 border-[#05E0D1]/20 p-12 text-center shadow-xl flex flex-col items-center justify-center" style={{ height: '600px', minHeight: '600px', maxHeight: '600px' }}>
        <div className="w-20 h-20 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Search className="text-[#1875FF]" size={36} />
        </div>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 font-display">No scheduled actions</h3>
        <p className="text-gray-700 text-sm font-medium">New tasks will appear as lead requests are processed</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-[#05E0D1]/5 rounded-xl border-2 border-[#05E0D1]/20 shadow-xl flex flex-col" style={{ height: '600px', minHeight: '600px', maxHeight: '600px' }}>
      <div className="p-6 border-b border-[#05E0D1]/10 bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 flex-shrink-0">
        <h3 className="text-lg font-extrabold text-gray-900 flex items-center space-x-2 font-display">
          <Clock className="text-[#1875FF]" size={20} />
          <span>Next Scheduled Actions</span>
        </h3>
        <p className="text-sm text-gray-700 mt-1 font-medium">
          {displayActions.length > 0 ? `${pendingTasks.length} action${pendingTasks.length !== 1 ? 's' : ''} queued` : 'No upcoming actions'}
        </p>
      </div>

      <div className="flex-1 p-6 overflow-hidden">
        {displayActions.length > 0 ? (
          <div className="space-y-2.5">
            {displayActions.map((action, index) => {
              const ActionIcon = action.icon;
              const isFirst = index === 0;
              return (
                <div key={action.taskId}>
                  <div
                    className={`flex items-center justify-between p-3.5 rounded-xl transition-all duration-300 border ${
                      isFirst
                        ? 'bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-[#05E0D1] shadow-lg'
                        : 'bg-gradient-to-r from-white to-[#05E0D1]/5 border-[#05E0D1]/20 hover:from-[#05E0D1]/10 hover:to-[#1875FF]/10 hover:border-[#05E0D1] hover:shadow-lg'
                    }`}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                        <ActionIcon className="text-[#1875FF]" size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{action.leadName}</p>
                        <p className="text-xs text-gray-600 truncate">{action.company}</p>
                        <p className="text-xs text-gray-700 flex items-center space-x-1 mt-0.5">
                          <Clock size={11} />
                          <span>{formatDistanceInTz(action.scheduledTime, { addSuffix: true })}</span>
                        </p>
                      </div>
                      <div className="flex-shrink-0 flex items-center space-x-2">
                        {isFirst && (
                          <div className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#05E0D1] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#05E0D1]"></span>
                          </div>
                        )}
                        <span className="text-xs font-semibold text-[#1875FF] bg-[#1875FF]/10 px-2 py-1 rounded">
                          {action.description}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full -mt-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mb-4">
              <Search className="text-[#1875FF]" size={28} />
            </div>
            <p className="text-gray-800 text-base font-bold">No scheduled actions at the moment</p>
            <p className="text-gray-500 text-sm mt-1.5 font-medium">Place a lead request and I'll get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadGenNextActionsPanel;
